import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Search, Eye, CheckCircle, XCircle, Clock, Download, ClipboardCheck, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CaseLogsTab({ token }) {
  const [caseLogs, setCaseLogs] = useState([]);
  const [submittedLogbooks, setSubmittedLogbooks] = useState([]);
  const [examiners, setExaminers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExaminer, setSelectedExaminer] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showLogbookDetail, setShowLogbookDetail] = useState(null);
  const [viewMode, setViewMode] = useState('logbooks'); // 'logbooks' or 'individual'

  const fetchData = async () => {
    try {
      const [logsRes, logbooksRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/case-logs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/submitted-logbooks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCaseLogs(logsRes.data);
      setSubmittedLogbooks(logbooksRes.data);
      setExaminers(usersRes.data.filter(u => u.role === 'examiner' || u.role === 'admin'));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const assignToExaminer = async (logId) => {
    if (!selectedExaminer) {
      toast.error('Please select an examiner');
      return;
    }
    try {
      await axios.put(`${API}/admin/case-logs/${logId}/assign?examiner_id=${selectedExaminer}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logbook assigned to examiner for review');
      setShowAssignModal(null);
      setSelectedExaminer('');
      fetchData();
    } catch (err) { toast.error('Failed to assign'); }
  };

  const viewLogbookDetail = async (logId) => {
    try {
      const res = await axios.get(`${API}/admin/logbook/${logId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setShowLogbookDetail(res.data);
    } catch (err) { 
      toast.error('Failed to load logbook details'); 
    }
  };

  const statusColor = (s) => {
    const m = { 
      submitted: 'bg-blue-100 text-blue-700', 
      assigned: 'bg-amber-100 text-amber-700',
      under_review: 'bg-purple-100 text-purple-700',
      scored: 'bg-green-100 text-green-700'
    };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading case logs...</p>;

  return (
    <div data-testid="admin-case-logs-tab">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'logbooks' ? 'default' : 'outline'}
            onClick={() => setViewMode('logbooks')}
            className={`rounded-sm text-sm ${viewMode === 'logbooks' ? 'bg-[#1B2A4A] text-white' : ''}`}
          >
            <ClipboardCheck className="h-4 w-4 mr-1" /> Submitted Logbooks ({submittedLogbooks.length})
          </Button>
          <Button 
            variant={viewMode === 'individual' ? 'default' : 'outline'}
            onClick={() => setViewMode('individual')}
            className={`rounded-sm text-sm ${viewMode === 'individual' ? 'bg-[#1B2A4A] text-white' : ''}`}
          >
            <FileText className="h-4 w-4 mr-1" /> All Case Logs ({caseLogs.length})
          </Button>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Assign Logbook to Examiner</h3>
              <p className="text-sm text-slate-500 mb-4">Select an examiner to review this logbook submission.</p>
              <div className="mb-4">
                <Label className="text-sm">Select Examiner</Label>
                <Select value={selectedExaminer} onValueChange={setSelectedExaminer}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Choose an examiner" /></SelectTrigger>
                  <SelectContent>
                    {examiners.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.full_name} ({e.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowAssignModal(null); setSelectedExaminer(''); }} className="rounded-sm">Cancel</Button>
                <Button onClick={() => assignToExaminer(showAssignModal)} className="bg-[#1B2A4A] text-white rounded-sm">Assign</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Logbook Detail Modal */}
      {showLogbookDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B2A4A]">Logbook Submission Details</h3>
                  <p className="text-sm text-slate-500">{showLogbookDetail.candidate_name} - {showLogbookDetail.candidate_email}</p>
                </div>
                <Button variant="ghost" onClick={() => setShowLogbookDetail(null)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="bg-slate-50 p-3 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Total Cases:</span>
                    <span className="font-bold text-[#1B2A4A] ml-2">{showLogbookDetail.total_cases}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Submitted:</span>
                    <span className="ml-2">{showLogbookDetail.submitted_at ? new Date(showLogbookDetail.submitted_at).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <Badge className={`ml-2 text-xs ${statusColor(showLogbookDetail.status)}`}>{showLogbookDetail.status}</Badge>
                  </div>
                </div>
                {showLogbookDetail.scoring && (
                  <div className="mt-3 pt-3 border-t">
                    <span className="text-slate-500">Score:</span>
                    <span className="font-bold text-green-600 ml-2">{showLogbookDetail.scoring.overall_score}/10</span>
                    {showLogbookDetail.scoring.feedback && (
                      <p className="text-sm text-slate-600 mt-1">Feedback: {showLogbookDetail.scoring.feedback}</p>
                    )}
                  </div>
                )}
              </div>

              <h4 className="font-semibold text-[#1B2A4A] mb-3">Case Entries ({showLogbookDetail.entries_snapshot?.length || 0})</h4>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold">Case ID</TableHead>
                      <TableHead className="text-xs font-bold">Procedure</TableHead>
                      <TableHead className="text-xs font-bold">Patient</TableHead>
                      <TableHead className="text-xs font-bold">Diagnosis</TableHead>
                      <TableHead className="text-xs font-bold">Outcome</TableHead>
                      <TableHead className="text-xs font-bold">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {showLogbookDetail.entries_snapshot?.map((entry, idx) => (
                      <TableRow key={entry.id || idx}>
                        <TableCell className="text-xs font-mono">{entry.case_id}</TableCell>
                        <TableCell className="text-sm">{entry.procedure_type || entry.procedure}</TableCell>
                        <TableCell className="text-sm">
                          {entry.patient_name} ({entry.age}y, {entry.gender})
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{entry.diagnosis}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${entry.complication ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {entry.outcome}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <div className="flex gap-2 justify-end mt-4 pt-4 border-t">
                {showLogbookDetail.status === 'submitted' && (
                  <Button 
                    onClick={() => { setShowAssignModal(showLogbookDetail.id); setShowLogbookDetail(null); }}
                    className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                  >
                    Assign to Examiner
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowLogbookDetail(null)} className="rounded-sm">Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Submitted Logbooks View */}
      {viewMode === 'logbooks' && (
        <>
          {submittedLogbooks.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No logbook submissions yet.</p>
              <p className="text-xs text-slate-400 mt-1">Candidates need to complete at least 5 cases to submit their logbook.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead className="text-xs font-bold">Candidate</TableHead>
                    <TableHead className="text-xs font-bold">Total Cases</TableHead>
                    <TableHead className="text-xs font-bold">Submitted</TableHead>
                    <TableHead className="text-xs font-bold">Assigned To</TableHead>
                    <TableHead className="text-xs font-bold">Status</TableHead>
                    <TableHead className="text-xs font-bold">Score</TableHead>
                    <TableHead className="text-xs font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submittedLogbooks.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <p className="text-sm font-medium text-[#1B2A4A]">{log.candidate_name}</p>
                        <p className="text-xs text-slate-500">{log.candidate_email}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700">{log.total_cases} cases</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {log.submitted_at ? new Date(log.submitted_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {log.examiner_name ? (
                          <p className="text-sm">{log.examiner_name}</p>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs rounded-sm ${statusColor(log.status)}`}>{log.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {log.scoring ? (
                          <span className="text-sm font-semibold text-green-600">{log.scoring.overall_score}/10</span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 px-2 text-xs rounded-sm" 
                            onClick={() => viewLogbookDetail(log.id)}
                            data-testid={`view-logbook-${log.id}`}
                          >
                            <Eye className="h-3 w-3 mr-1" /> View
                          </Button>
                          {log.status === 'submitted' && (
                            <Button 
                              size="sm" 
                              className="h-7 px-2 bg-[#1B2A4A] text-white text-xs rounded-sm" 
                              onClick={() => setShowAssignModal(log.id)}
                              data-testid={`assign-logbook-${log.id}`}
                            >
                              Assign
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}

      {/* Individual Case Logs View */}
      {viewMode === 'individual' && (
        <>
          {caseLogs.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No case logs submitted yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead className="text-xs font-bold">Candidate</TableHead>
                    <TableHead className="text-xs font-bold">Patient</TableHead>
                    <TableHead className="text-xs font-bold">Procedure</TableHead>
                    <TableHead className="text-xs font-bold">Date</TableHead>
                    <TableHead className="text-xs font-bold">Status</TableHead>
                    <TableHead className="text-xs font-bold">Score</TableHead>
                    <TableHead className="text-xs font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {caseLogs.filter(log => !log.entries_snapshot).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <p className="text-sm font-medium text-[#1B2A4A]">{log.candidate_name}</p>
                        <p className="text-xs text-slate-500">{log.candidate_email}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{log.patient_name}</p>
                        <p className="text-xs text-slate-500">{log.patient_age}y, {log.patient_gender}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs rounded-sm">{log.procedure_type}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(log.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs rounded-sm ${statusColor(log.status)}`}>{log.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {log.scoring ? (
                          <span className="text-sm font-semibold">{log.scoring.overall_score}/10</span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.status === 'submitted' && (
                          <Button size="sm" className="h-6 px-2 bg-[#1B2A4A] text-white text-xs rounded-sm" onClick={() => setShowAssignModal(log.id)}>
                            Assign
                          </Button>
                        )}
                        {log.status === 'assigned' && (
                          <span className="text-xs text-amber-600">Pending scoring</span>
                        )}
                        {log.status === 'scored' && (
                          <span className="text-xs text-green-600">Completed</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CaseLogsTab;
